/* eslint-disable complexity */
import React from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import Box from 'carbon-react/lib/components/box';
import Message from 'carbon-react/lib/components/message';

import { UILoader } from '@futrli/ui-components';

import { AiInsights } from './ai-insights';
import { mapToAccountantData } from './mappers/accountant-mapper';
import { mapToBusinessOwnerData } from './mappers/business-owner-mapper';
import { useAiAnalysis } from './hooks/useAiAnalysis';

interface AiInsightsContainerProps {
  isActive?: boolean;
}

export const AiInsightsContainer = ({ isActive = true }: AiInsightsContainerProps) => {
  const { workspaceId = '', organisationId = '' } = useParams();
  const { analysisResponse, isLoading, isPending, hasFailed, hasTimedOut, refreshAnalysis } = useAiAnalysis(
    workspaceId,
    organisationId,
    isActive
  );

  const intl = useIntl();

  if (hasTimedOut) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <Message variant="error" open showCloseIcon={false}>
          {intl.formatMessage({
            id: 'aiInsights.error_timeout',
            defaultMessage: 'Analysis currently not available, please try later.'
          })}
        </Message>
      </Box>
    );
  }

  if (hasFailed) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <Message variant="error" open showCloseIcon={false}>
          {intl.formatMessage({
            id: 'aiInsights.error_contact_support',
            defaultMessage: 'Something went wrong generating your analysis. Please contact support.'
          })}
        </Message>
      </Box>
    );
  }

  if (isLoading || isPending) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <UILoader
          label={
            !isLoading && isPending
              ? intl.formatMessage({ id: 'aiInsights.loading', defaultMessage: 'Generating your analysis' })
              : undefined
          }
        />
      </Box>
    );
  }

  const { analysis } = analysisResponse!;

  const isAccountant = analysis.variant === 'accountant';
  const data = isAccountant ? mapToAccountantData(analysis, intl) : mapToBusinessOwnerData(analysis, intl);

  return (
    <AiInsights
      data={data}
      onAnalysisRefresh={refreshAnalysis}
      organisationId={organisationId}
      variant={isAccountant ? 'accountant' : 'businessOwner'}
      workspaceId={workspaceId}
    />
  );
};
